import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './ERDashboard.css';
import './TCOCDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');
const formatPersonYears = (value) => Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const formatLOS = (value) => Number(value || 0).toFixed(1);
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatPercent = (value) => `${(Number(value || 0)).toFixed(1)}%`;
const formatDecimal = (value) => (Number(value || 0)).toFixed(3);

function TCOCGeneral({ benchmarkData }) {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState({
    ESRD: { by3_risk: 0, py_risk: 0, risk_trend: 0, nat_reg_trend: 0, final_trend: 0 },
    DIS: { by3_risk: 0, py_risk: 0, risk_trend: 0, nat_reg_trend: 0, final_trend: 0 },
    AD: { by3_risk: 0, py_risk: 0, risk_trend: 0, nat_reg_trend: 0, final_trend: 0 },
    ND: { by3_risk: 0, py_risk: 0, risk_trend: 0, nat_reg_trend: 0, final_trend: 0 }
  });

  const [savingsData, setSavingsData] = useState({
    ESRD: { bmk_pmpy: 0, final_trend: 0, updated_bmk_pmpy: 0, py_person_years: 0, py_pmpy: 0, pmpy_savings: 0, total_savings: 0 },
    DIS: { bmk_pmpy: 0, final_trend: 0, updated_bmk_pmpy: 0, py_person_years: 0, py_pmpy: 0, pmpy_savings: 0, total_savings: 0 },
    AD: { bmk_pmpy: 0, final_trend: 0, updated_bmk_pmpy: 0, py_person_years: 0, py_pmpy: 0, pmpy_savings: 0, total_savings: 0 },
    ND: { bmk_pmpy: 0, final_trend: 0, updated_bmk_pmpy: 0, py_person_years: 0, py_pmpy: 0, pmpy_savings: 0, total_savings: 0 },
    TOTAL: { total_savings: 0 }
  });

  const [utilizationMetrics, setUtilizationMetrics] = useState({
    inp_los: 0,
    snf_los: 0,
    hha_los: 0,
    hos_los: 0,
    inp_30d_readme_pct: 0,
    snf_30d_readme_pct: 0,
    hha_30d_readme_pct: 0,
    er_30d_readme_pct: 0,
    er_to_inp_30d_readme_pct: 0,
    inp_admits: 0,
    snf_admits: 0,
    hha_admits: 0,
    hos_admits: 0,
    er_visits: 0
  });

  const [costMetrics, setCostMetrics] = useState({
    snf_pmpy: 0,
    hha_pmpy: 0,
    hos_pmpy: 0,
    phy_pmpy: 0,
    inp_pmpy: 0,
    dme_pmpy: 0,
    op_pmpy: 0,
    er_pmpy: 0,
    avoidable_er_pmpy: 0
  });

  useEffect(() => {
    fetchGeneralData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkData]);

  const fetchGeneralData = async () => {
    setLoading(true);
    try {
      console.log('Fetching TCOC General data...');
      const response = await axios.get(`${API_BASE_URL}/api/tcoc/general`, {
        params: {
          project: PROJECT_ID,
          dataset: DATASET_NAME
        }
      });

      console.log('TCOC General response:', response.data);

      if (response.data) {
        const data = response.data;
        
        // Calculate trend data
        const calculatedTrendData = {};
        ['ESRD', 'DIS', 'AD', 'ND'].forEach(cohort => {
          const by3Risk = benchmarkData.riskScores[cohort] || 1;
          const pyRisk = data.cohort_metrics[cohort]?.avg_risk_score || 1;
          const natRegTrend = benchmarkData.trends[cohort] || 1;
          const rawRiskTrend = pyRisk / by3Risk;
          const riskTrend = Math.min(rawRiskTrend, 1.03);
          const finalTrend = riskTrend * natRegTrend;

          calculatedTrendData[cohort] = {
            by3_risk: by3Risk,
            py_risk: pyRisk,
            risk_trend: riskTrend,
            nat_reg_trend: natRegTrend,
            final_trend: finalTrend
          };
        });
        setTrendData(calculatedTrendData);

        // Calculate savings data
        const calculatedSavingsData = {};
        let totalSavings = 0;
        ['ESRD', 'DIS', 'AD', 'ND'].forEach(cohort => {
          const bmkPmpy = benchmarkData.benchmarks[cohort] || 0;
          const finalTrend = calculatedTrendData[cohort].final_trend;
          const updatedBmkPmpy = bmkPmpy * finalTrend;
          const pyPersonYears = data.cohort_metrics[cohort]?.person_years || 0;
          const pyPmpy = data.cohort_metrics[cohort]?.pmpy || 0;
          const pmpySavings = updatedBmkPmpy - pyPmpy;
          const cohortTotalSavings = pmpySavings * pyPersonYears;
          totalSavings += cohortTotalSavings;

          calculatedSavingsData[cohort] = {
            bmk_pmpy: bmkPmpy,
            final_trend: finalTrend,
            updated_bmk_pmpy: updatedBmkPmpy,
            py_person_years: pyPersonYears,
            py_pmpy: pyPmpy,
            pmpy_savings: pmpySavings,
            total_savings: cohortTotalSavings
          };
        });
        calculatedSavingsData.TOTAL = { total_savings: totalSavings };
        setSavingsData(calculatedSavingsData);

        setUtilizationMetrics(data.utilization_metrics || {});
        setCostMetrics(data.cost_metrics || {});
      }
    } catch (error) {
      console.error('Error fetching TCOC general data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSavingsColor = (value) => {
    if (value > 0) return 'green';
    if (value < 0) return 'red';
    return 'black';
  };

  if (loading) {
    return <div className="loading">Loading TCOC General data...</div>;
  }

  return (
    <div style={{ width: '100%', margin: 0, padding: 0, backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      <h1 className="dashboard-title" style={{ paddingLeft: '20px', paddingTop: '20px' }}>TCOC PY GENERAL</h1>
        
        {/* Trend and Savings Tables Side by Side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px', padding: '0 20px' }}>
          {/* Trend Table */}
          <div className="table-section">
            <h2 style={{ marginBottom: '20px', color: '#333' }}>Trend Table</h2>
            <table className="summary-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>ESRD</th>
                <th>DIS</th>
                <th>AD</th>
                <th>ND</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ backgroundColor: '#e3f2fd' }}>
                <td>BY3 RISK</td>
                <td>{formatRiskScore(trendData.ESRD.by3_risk)}</td>
                <td>{formatRiskScore(trendData.DIS.by3_risk)}</td>
                <td>{formatRiskScore(trendData.AD.by3_risk)}</td>
                <td>{formatRiskScore(trendData.ND.by3_risk)}</td>
              </tr>
              <tr style={{ backgroundColor: '#fff3e0' }}>
                <td>PY RISK</td>
                <td>{formatRiskScore(trendData.ESRD.py_risk)}</td>
                <td>{formatRiskScore(trendData.DIS.py_risk)}</td>
                <td>{formatRiskScore(trendData.AD.py_risk)}</td>
                <td>{formatRiskScore(trendData.ND.py_risk)}</td>
              </tr>
              <tr style={{ backgroundColor: '#f3e5f5' }}>
                <td>RISK TREND</td>
                <td>{formatDecimal(trendData.ESRD.risk_trend)}</td>
                <td>{formatDecimal(trendData.DIS.risk_trend)}</td>
                <td>{formatDecimal(trendData.AD.risk_trend)}</td>
                <td>{formatDecimal(trendData.ND.risk_trend)}</td>
              </tr>
              <tr style={{ backgroundColor: '#fce4ec' }}>
                <td>NAT REG TREND</td>
                <td>{formatDecimal(trendData.ESRD.nat_reg_trend)}</td>
                <td>{formatDecimal(trendData.DIS.nat_reg_trend)}</td>
                <td>{formatDecimal(trendData.AD.nat_reg_trend)}</td>
                <td>{formatDecimal(trendData.ND.nat_reg_trend)}</td>
              </tr>
              <tr style={{ backgroundColor: '#c8e6c9', fontWeight: 'bold' }}>
                <td><strong>FINAL TREND</strong></td>
                <td><strong>{formatDecimal(trendData.ESRD.final_trend)}</strong></td>
                <td><strong>{formatDecimal(trendData.DIS.final_trend)}</strong></td>
                <td><strong>{formatDecimal(trendData.AD.final_trend)}</strong></td>
                <td><strong>{formatDecimal(trendData.ND.final_trend)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Savings Table */}
        <div className="table-section">
          <h2 style={{ marginBottom: '20px', color: '#333' }}>Savings Table</h2>
          <table className="summary-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>ESRD</th>
                <th>DIS</th>
                <th>AD</th>
                <th>ND</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>BMK PMPY</td>
                <td>{formatCurrency(savingsData.ESRD.bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.DIS.bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.AD.bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.ND.bmk_pmpy)}</td>
              </tr>
              <tr>
                <td>FINAL TREND</td>
                <td>{formatDecimal(savingsData.ESRD.final_trend)}</td>
                <td>{formatDecimal(savingsData.DIS.final_trend)}</td>
                <td>{formatDecimal(savingsData.AD.final_trend)}</td>
                <td>{formatDecimal(savingsData.ND.final_trend)}</td>
              </tr>
              <tr>
                <td>UPDATED BMK PMPY</td>
                <td>{formatCurrency(savingsData.ESRD.updated_bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.DIS.updated_bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.AD.updated_bmk_pmpy)}</td>
                <td>{formatCurrency(savingsData.ND.updated_bmk_pmpy)}</td>
              </tr>
              <tr>
                <td>PY PERSON YEARS</td>
                <td>{formatPersonYears(savingsData.ESRD.py_person_years)}</td>
                <td>{formatPersonYears(savingsData.DIS.py_person_years)}</td>
                <td>{formatPersonYears(savingsData.AD.py_person_years)}</td>
                <td>{formatPersonYears(savingsData.ND.py_person_years)}</td>
              </tr>
              <tr>
                <td>PY PMPY</td>
                <td>{formatCurrency(savingsData.ESRD.py_pmpy)}</td>
                <td>{formatCurrency(savingsData.DIS.py_pmpy)}</td>
                <td>{formatCurrency(savingsData.AD.py_pmpy)}</td>
                <td>{formatCurrency(savingsData.ND.py_pmpy)}</td>
              </tr>
              <tr>
                <td>PMPY SAVINGS</td>
                <td style={{ color: getSavingsColor(savingsData.ESRD.pmpy_savings) }}>
                  {formatCurrency(savingsData.ESRD.pmpy_savings)}
                </td>
                <td style={{ color: getSavingsColor(savingsData.DIS.pmpy_savings) }}>
                  {formatCurrency(savingsData.DIS.pmpy_savings)}
                </td>
                <td style={{ color: getSavingsColor(savingsData.AD.pmpy_savings) }}>
                  {formatCurrency(savingsData.AD.pmpy_savings)}
                </td>
                <td style={{ color: getSavingsColor(savingsData.ND.pmpy_savings) }}>
                  {formatCurrency(savingsData.ND.pmpy_savings)}
                </td>
              </tr>
              <tr>
                <td><strong>TOTAL SAVINGS</strong></td>
                <td style={{ color: getSavingsColor(savingsData.ESRD.total_savings) }}>
                  <strong>{formatCurrency(savingsData.ESRD.total_savings)}</strong>
                </td>
                <td style={{ color: getSavingsColor(savingsData.DIS.total_savings) }}>
                  <strong>{formatCurrency(savingsData.DIS.total_savings)}</strong>
                </td>
                <td style={{ color: getSavingsColor(savingsData.AD.total_savings) }}>
                  <strong>{formatCurrency(savingsData.AD.total_savings)}</strong>
                </td>
                <td style={{ color: getSavingsColor(savingsData.ND.total_savings) }}>
                  <strong>{formatCurrency(savingsData.ND.total_savings)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="total-savings-summary" style={{ 
            marginTop: '20px', 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            color: getSavingsColor(savingsData.TOTAL.total_savings)
          }}>
            TOTAL SAVINGS (ALL COHORTS): {formatCurrency(savingsData.TOTAL.total_savings)}
          </div>
        </div>
        </div>

      {/* Utilization and Cost Metrics Tables Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px', padding: '0 20px' }}>
        <div className="table-section">
          <h2 style={{ marginBottom: '20px', color: '#333' }}>Utilization Metrics</h2>
          <table className="summary-table">
            <tbody>
              <tr><td>INP LOS</td><td>{formatLOS(utilizationMetrics.inp_los)}</td></tr>
              <tr><td>SNF LOS</td><td>{formatLOS(utilizationMetrics.snf_los)}</td></tr>
              <tr><td>HHA LOS</td><td>{formatLOS(utilizationMetrics.hha_los)}</td></tr>
              <tr><td>HOS LOS</td><td>{formatLOS(utilizationMetrics.hos_los)}</td></tr>
              <tr><td>INP 30D README %</td><td>{formatPercent(utilizationMetrics.inp_30d_readme_pct)}</td></tr>
              <tr><td>SNF 30D README %</td><td>{formatPercent(utilizationMetrics.snf_30d_readme_pct)}</td></tr>
              <tr><td>HHA 30D README %</td><td>{formatPercent(utilizationMetrics.hha_30d_readme_pct)}</td></tr>
              <tr><td>ER 30D README %</td><td>{formatPercent(utilizationMetrics.er_30d_readme_pct)}</td></tr>
              <tr><td>ER TO INP 30D README %</td><td>{formatPercent(utilizationMetrics.er_to_inp_30d_readme_pct)}</td></tr>
              <tr><td>INP ADMITS</td><td>{formatNumber(utilizationMetrics.inp_admits)}</td></tr>
              <tr><td>SNF ADMITS</td><td>{formatNumber(utilizationMetrics.snf_admits)}</td></tr>
              <tr><td>HHA ADMITS</td><td>{formatNumber(utilizationMetrics.hha_admits)}</td></tr>
              <tr><td>HOS ADMITS</td><td>{formatNumber(utilizationMetrics.hos_admits)}</td></tr>
              <tr><td>ER VISITS</td><td>{formatNumber(utilizationMetrics.er_visits)}</td></tr>
            </tbody>
          </table>
      </div>

      <div className="table-section">
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Cost Metrics (PMPY)</h2>
        <table className="summary-table">
            <tbody>
              {(() => {
                const totalPY = ['ESRD', 'DIS', 'AD', 'ND'].reduce((s, c) => s + (savingsData[c]?.py_person_years || 0), 0);
                const totalCostSum = ['ESRD', 'DIS', 'AD', 'ND'].reduce((s, c) => s + (savingsData[c]?.py_pmpy || 0) * (savingsData[c]?.py_person_years || 0), 0);
                const totalPmpy = totalPY > 0 ? totalCostSum / totalPY : 0;
                return (
                  <tr style={{ backgroundColor: '#c8e6c9', fontWeight: 'bold' }}>
                    <td><strong>TOTAL PMPY</strong></td>
                    <td><strong>{formatCurrency(totalPmpy)}</strong></td>
                  </tr>
                );
              })()}
              <tr><td>SNF PMPY</td><td>{formatCurrency(costMetrics.snf_pmpy)}</td></tr>
              <tr><td>HHA PMPY</td><td>{formatCurrency(costMetrics.hha_pmpy)}</td></tr>
              <tr><td>HOS PMPY</td><td>{formatCurrency(costMetrics.hos_pmpy)}</td></tr>
              <tr><td>PHY PMPY</td><td>{formatCurrency(costMetrics.phy_pmpy)}</td></tr>
              <tr><td>INP PMPY</td><td>{formatCurrency(costMetrics.inp_pmpy)}</td></tr>
              <tr><td>DME PMPY</td><td>{formatCurrency(costMetrics.dme_pmpy)}</td></tr>
              <tr><td>OP PMPY</td><td>{formatCurrency(costMetrics.op_pmpy)}</td></tr>
              <tr><td>ER PMPY</td><td>{formatCurrency(costMetrics.er_pmpy)}</td></tr>
              <tr><td>AVOIDABLE ER PMPY</td><td>{formatCurrency(costMetrics.avoidable_er_pmpy)}</td></tr>
            </tbody>
          </table>
        </div>
        </div>
    </div>
  );
}

export default TCOCGeneral;
